/*
  plp-side-drawer.js

  UI behaviors for the PLP grid wrapper:
    1. Drawer open/close on mobile (data-filter-open on .c-product-list-page).
    2. Hide/show rail on desktop (data-rail-hidden on .c-product-list-page).
    3. Sort dropdown open/close (data-open on .c-plp-sort-bar__dropdown).
    4. Sort option click -> updates ?sort_by=... in URL + refetches grid.
    5. Per-filter-group "Show all / Show fewer" reveal (data-opts-expanded on .c-filter-group).
    6. Mobile staged-apply: filter clicks inside the drawer accumulate into a
       local staged set instead of committing to the URL immediately. The user
       commits via the APPLY FILTERS button (sticky footer) or discards by
       closing the drawer any other way (X, backdrop, Escape). Desktop is
       unchanged: filter clicks fall through to whichever engine owns the page
       and apply immediately.

  Per-facet accordion open/close is handled natively by <details>/<summary>.
  Filter clicks (toggling values in/out of the URL) on desktop go through the
  owning engine via the data-filter-tile contract: plp-searchanise-controller.js
  once it has won ownership of a collection page (or on /search), otherwise
  url-filter-controller.js. On mobile, the capture-phase handler below
  intercepts the same clicks before either engine's bubble handler sees them.

  IIFE pattern matches theme.js.
*/

(function () {
  'use strict';

  /* Open state is tracked in a closure because the wrapper's data attribute
     lives inside the .c-product-list-page section, which url-filter-controller
     swaps out wholesale after each filter click. Without this, the drawer
     would lose its open state on every section swap while body.style.overflow
     remained 'hidden', creating a permanent scroll-lock leak. */
  let drawerWasOpen = false;

  /* Mobile staged-apply state. Null while drawer is closed; populated when
     the drawer opens on a mobile viewport. See section 6 in the file header
     for the contract. */
  const STAGE_QUERY = window.matchMedia('(width < 900px)');
  let stagedState = null;          // Map<paramName, Set<value>> | null
  let appliedState = null;         // Map snapshot taken at drawer-open
  let stagedDestinationPath = null; // pathname override for maincategory clicks

  function getWrapper() {
    return document.querySelector('.c-product-list-page');
  }

  function getDrawer() {
    const wrapper = getWrapper();
    return wrapper ? wrapper.querySelector('[data-plp-side-drawer]') : null;
  }

  /* ─── Mobile drawer open/close ──────────────────────────────────── */

  function setDrawerOpen(open) {
    drawerWasOpen = open;
    const wrapper = getWrapper();
    if (!wrapper) return;
    if (typeof window.toggleData === 'function') {
      window.toggleData(wrapper, 'filterOpen', open);
    } else {
      wrapper.dataset.filterOpen = open ? 'true' : 'false';
    }
    const trigger = wrapper.querySelector('[data-filter-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    /* Scroll-lock. <html> is the default scroll container in modern browsers,
       so the body-only lock that shipped pre-S121 was silently a no-op.
       Locking both removes the scroll-under behind the drawer. */
    document.documentElement.style.overflow = open ? 'hidden' : '';
    document.body.style.overflow = open ? 'hidden' : '';

    if (open) {
      if (STAGE_QUERY.matches) snapshotStagedState();
    } else if (stagedState !== null) {
      discardStagedState();
    }
  }

  /* ─── Mobile staged-apply helpers ────────────────────────────────── */

  function readFiltersFromUrl() {
    const map = new Map();
    const params = new URL(window.location.href).searchParams;
    params.forEach(function (value, key) {
      if (key.indexOf('filter.') !== 0) return;
      if (!map.has(key)) map.set(key, new Set());
      map.get(key).add(value);
    });
    return map;
  }

  function cloneFilterMap(src) {
    const out = new Map();
    src.forEach(function (set, key) { out.set(key, new Set(set)); });
    return out;
  }

  function filterMapsDiverge(a, b) {
    if (a.size !== b.size) return true;
    for (const [key, aSet] of a) {
      const bSet = b.get(key);
      if (!bSet || bSet.size !== aSet.size) return true;
      for (const v of aSet) if (!bSet.has(v)) return true;
    }
    return false;
  }

  function setStagedChangesFlag() {
    const drawer = getDrawer();
    if (!drawer) return;
    const diverges = stagedState !== null && filterMapsDiverge(stagedState, appliedState);
    if (typeof window.toggleData === 'function') {
      window.toggleData(drawer, 'stagedChanges', diverges);
    } else {
      drawer.dataset.stagedChanges = diverges ? 'true' : 'false';
    }
  }

  function renderStagedSelections() {
    const drawer = getDrawer();
    if (!drawer || stagedState === null) return;
    drawer.querySelectorAll('[data-filter-tile]').forEach(function (tile) {
      const param = tile.getAttribute('data-filter-param');
      const value = tile.getAttribute('data-filter-value');
      const set = stagedState.get(param);
      const isSelected = !!(set && set.has(value));
      tile.setAttribute('data-selected', isSelected ? 'true' : 'false');
      tile.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
    drawer.querySelectorAll('[data-category-tile-link]').forEach(function (link) {
      const value = link.getAttribute('data-category-value');
      const set = stagedState.get('filter.p.m.custom.maincategory');
      const isSelected = !!(set && set.has(value));
      link.setAttribute('data-selected', isSelected ? 'true' : 'false');
      if (isSelected) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  function snapshotStagedState() {
    appliedState = readFiltersFromUrl();
    stagedState = cloneFilterMap(appliedState);
    stagedDestinationPath = null;
    renderStagedSelections();
    setStagedChangesFlag();
  }

  function discardStagedState() {
    /* Reset staged to applied, redraw tiles from the applied snapshot, then
       clear closure state. Called when the drawer closes without Apply. */
    if (stagedState !== null && appliedState !== null) {
      stagedState = cloneFilterMap(appliedState);
      renderStagedSelections();
    }
    stagedState = null;
    appliedState = null;
    stagedDestinationPath = null;
    setStagedChangesFlag();
  }

  function stageFilter(paramName, value, mode) {
    if (stagedState === null) return;
    const set = stagedState.get(paramName);
    if (mode === 'single') {
      const alreadyOn = !!(set && set.has(value));
      if (alreadyOn) {
        stagedState.delete(paramName);
      } else {
        stagedState.set(paramName, new Set([value]));
      }
    } else {
      const next = set ? new Set(set) : new Set();
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      if (next.size === 0) {
        stagedState.delete(paramName);
      } else {
        stagedState.set(paramName, next);
      }
    }
    renderStagedSelections();
    setStagedChangesFlag();
  }

  function buildAppliedUrl() {
    const url = new URL(window.location.href);
    const maincategorySet = stagedState.get('filter.p.m.custom.maincategory');
    if (maincategorySet && maincategorySet.size > 0 && stagedDestinationPath) {
      url.pathname = stagedDestinationPath;
    }
    Array.from(url.searchParams.keys())
      .filter(function (k) { return k.indexOf('filter.') === 0; })
      .forEach(function (k) { url.searchParams.delete(k); });
    stagedState.forEach(function (set, key) {
      set.forEach(function (value) { url.searchParams.append(key, value); });
    });
    url.searchParams.delete('page');
    return url;
  }

  function applyStagedState() {
    if (stagedState === null) return;
    const url = buildAppliedUrl();
    /* If the apply path crosses to a different collection (maincategory
       redirect to /collections/all), fall through to a full navigation
       because url-filter-controller swaps the SAME section, not a new one. */
    const samePath = url.pathname === window.location.pathname;
    /* Collections load both engines. searchanisePlpRefresh exists only once
       Searchanise has won ownership, so prefer it and fall back to the native
       urlFilterRefreshGrid. Checked at call time, never captured at bind time. */
    const sameTemplateRefresh =
      typeof window.searchanisePlpRefresh === 'function'
        ? window.searchanisePlpRefresh
        : window.urlFilterRefreshGrid;
    if (samePath && typeof sameTemplateRefresh === 'function') {
      window.history.pushState({}, '', url);
      stagedState = null;
      appliedState = null;
      stagedDestinationPath = null;
      setStagedChangesFlag();
      sameTemplateRefresh();
      setDrawerOpen(false);
    } else {
      /* Cross-template navigation (e.g. /search -> /collections/all when a
         maincategory pill is applied). Full page load because the section
         render API can't render a different template than the current page. */
      window.location.assign(url.toString());
    }
  }

  /* After url-filter-controller swaps the section, the new wrapper has
     data-filter-open="false". If the drawer was open before the swap, the
     user is mid-filtering — re-project the open state onto the fresh DOM. */
  function reapplyDrawerStateAfterSwap() {
    if (drawerWasOpen) setDrawerOpen(true);
    applyFacetReveal(document);
  }

  function isDrawerOpen() {
    const wrapper = getWrapper();
    if (!wrapper) return false;
    return wrapper.dataset.filterOpen === 'true';
  }

  /* ─── Desktop rail hide/show ────────────────────────────────────── */

  function toggleRailHidden(button) {
    const wrapper = getWrapper();
    if (!wrapper) return;
    const next = wrapper.dataset.railHidden === 'true' ? 'false' : 'true';
    if (typeof window.toggleData === 'function') {
      window.toggleData(wrapper, 'railHidden', next === 'true');
    } else {
      wrapper.dataset.railHidden = next;
    }
    if (button) {
      button.setAttribute('aria-pressed', next);
      const hideText = button.querySelector('[data-rail-toggle-text-hide]');
      const showText = button.querySelector('[data-rail-toggle-text-show]');
      if (hideText && showText) {
        hideText.hidden = next === 'true';
        showText.hidden = next !== 'true';
      }
    }
  }

  /* ─── Sort dropdown open/close + select ─────────────────────────── */

  function setSortDropdownOpen(dropdown, open) {
    if (!dropdown) return;
    if (typeof window.toggleData === 'function') {
      window.toggleData(dropdown, 'open', open);
    } else {
      dropdown.dataset.open = open ? 'true' : 'false';
    }
    const trigger = dropdown.querySelector('[data-sort-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function closeAllSortDropdowns() {
    document.querySelectorAll('[data-sort-dropdown]').forEach(function (d) {
      setSortDropdownOpen(d, false);
    });
  }

  function applySortValue(value) {
    closeAllSortDropdowns();
    /* When the Searchanise controller owns the grid (/search + collections) it
       handles [data-sort-option] clicks itself (state + URL + client refetch), so
       the drawer only closes the dropdown and defers to avoid double-handling. */
    if (typeof window.searchanisePlpRefresh === 'function') return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    window.history.pushState({}, '', url);
    if (typeof window.urlFilterRefreshGrid === 'function') {
      window.urlFilterRefreshGrid();
    } else {
      window.location.reload();
    }
  }

  /* ─── Show all / Show fewer ─────────────────────────────────────── */

  /* Which facets the shopper expanded, by data-facet key. Both engines rebuild
     the rail collapsed on every filter change (Searchanise re-renders it, the
     native engine swaps the whole section), so the reveal has to be remembered
     here and re-applied after. Facets are multi-select: collapsing mid-selection
     hides the options most likely to be picked next. */
  const expandedFacets = new Set();

  function setShowAll(facet, expanded) {
    if (typeof window.toggleData === 'function') {
      window.toggleData(facet, 'optsExpanded', expanded);
    } else {
      facet.dataset.optsExpanded = expanded ? 'true' : 'false';
    }
    const button = facet.querySelector('[data-facet-show-all]');
    if (!button) return;
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    const showAllText = button.querySelector('[data-show-all-text]');
    const showLessText = button.querySelector('[data-show-less-text]');
    if (showAllText && showLessText) {
      showAllText.hidden = expanded;
      showLessText.hidden = !expanded;
    }
  }

  function toggleShowAll(facet, button) {
    if (!facet || !button) return;
    const next = facet.dataset.optsExpanded !== 'true';
    if (next) {
      expandedFacets.add(facet.dataset.facet);
    } else {
      expandedFacets.delete(facet.dataset.facet);
    }
    setShowAll(facet, next);
  }

  /* Re-apply the remembered reveal to freshly rendered filter groups. Called
     after a native section swap and by the Searchanise controller's renderRail. */
  function applyFacetReveal(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.c-filter-group[data-facet]').forEach(function (facet) {
      const expanded = expandedFacets.has(facet.dataset.facet);
      if (expanded) setShowAll(facet, true);
    });
  }

  window.PlpFacetReveal = { apply: applyFacetReveal };

  /* ─── Click dispatch ────────────────────────────────────────────── */

  function handleClick(event) {
    const sortOption = event.target.closest('[data-sort-option]');
    if (sortOption) {
      applySortValue(sortOption.getAttribute('data-sort-value'));
      return;
    }

    const sortTrigger = event.target.closest('[data-sort-trigger]');
    if (sortTrigger) {
      const dropdown = sortTrigger.closest('[data-sort-dropdown]');
      const isOpen = dropdown && dropdown.dataset.open === 'true';
      closeAllSortDropdowns();
      if (!isOpen) setSortDropdownOpen(dropdown, true);
      return;
    }

    if (!event.target.closest('[data-sort-dropdown]')) {
      closeAllSortDropdowns();
    }

    const railToggle = event.target.closest('[data-rail-toggle]');
    if (railToggle) { toggleRailHidden(railToggle); return; }

    const trigger = event.target.closest('[data-filter-trigger]');
    if (trigger) { setDrawerOpen(!isDrawerOpen()); return; }

    const close = event.target.closest('[data-filter-close]');
    if (close) { setDrawerOpen(false); return; }

    const backdrop = event.target.closest('[data-filter-backdrop]');
    if (backdrop) { setDrawerOpen(false); return; }

    const showAll = event.target.closest('[data-facet-show-all]');
    if (showAll) {
      const facet = showAll.closest('.c-filter-group');
      toggleShowAll(facet, showAll);
      return;
    }
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      closeAllSortDropdowns();
      if (isDrawerOpen()) setDrawerOpen(false);
    }
  }

  /* Capture-phase click handler for the mobile staged-apply path. Runs before
     url-filter-controller's bubble handler, so on mobile + drawer-open we
     can prevent the immediate-commit path and stage the change locally. */
  function handleStagingCapture(event) {
    if (!STAGE_QUERY.matches) return;
    if (!isDrawerOpen()) return;

    const drawer = getDrawer();
    if (!drawer || !drawer.contains(event.target)) return;

    const applyBtn = event.target.closest('[data-plp-apply-filters]');
    if (applyBtn) {
      event.preventDefault();
      event.stopPropagation();
      applyStagedState();
      return;
    }

    /* Clear all stages an empty filter set — the user still has to hit
       APPLY to commit. Without this intercept the anchor would navigate
       immediately on collection pages or the controller would commit on
       search, bypassing the staged-apply contract. */
    const clearAll = event.target.closest('[data-plp-clear-all]');
    if (clearAll) {
      event.preventDefault();
      event.stopPropagation();
      if (stagedState === null) snapshotStagedState();
      stagedState = new Map();
      stagedDestinationPath = null;
      renderStagedSelections();
      setStagedChangesFlag();
      return;
    }

    const tile = event.target.closest('[data-filter-tile]');
    if (tile) {
      const param = tile.getAttribute('data-filter-param');
      const value = tile.getAttribute('data-filter-value');
      const mode = tile.getAttribute('data-filter-mode') || 'multi';
      if (!param || !value) return;
      event.preventDefault();
      event.stopPropagation();
      if (stagedState === null) snapshotStagedState();
      stageFilter(param, value, mode);
      return;
    }

    const categoryLink = event.target.closest('[data-category-tile-link]');
    if (categoryLink) {
      const value = categoryLink.getAttribute('data-category-value');
      const href = categoryLink.getAttribute('href');
      if (!value) return;
      event.preventDefault();
      event.stopPropagation();
      if (href) {
        try {
          const parsed = new URL(href, window.location.href);
          stagedDestinationPath = parsed.pathname;
        } catch { /* ignore parse failures, fall back to current path */ }
      }
      if (stagedState === null) snapshotStagedState();
      stageFilter('filter.p.m.custom.maincategory', value, 'single');
      return;
    }
  }

  function init() {
    document.addEventListener('click', handleStagingCapture, true);
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('urlfilter:change', reapplyDrawerStateAfterSwap);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
